import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { loadRobotoFont } from './fonts/Roboto-Regular';

const CONDITION_LABELS = {
  excellent: 'Doskonały',
  good: 'Dobry',
  fair: 'Zadowalający',
  poor: 'Zły',
};

const INSPECTION_TYPE_LABELS = {
  'move-in': 'Wprowadzenie',
  periodic: 'Okresowa',
  'move-out': 'Wyprowadzenie',
};

/**
 * Format property address for display
 * @param {Object} property - Property object
 * @returns {string} Formatted address
 */
function formatPropertyAddress(property) {
  const parts = [
    property.street,
    property.building_number,
    property.apartment_number,
    property.postal_code,
    property.city,
  ].filter(Boolean);

  return parts.join(', ');
}

/**
 * Generate PDF for inspection
 * @param {Object} inspection - Inspection data with rooms and items
 * @param {Object} property - Property data
 * @returns {void} Downloads PDF file
 */
export async function generateInspectionPDF(inspection, property) {
  const doc = new jsPDF();

  // Load Roboto font for Polish character support
  loadRobotoFont(doc);
  doc.setFont('Roboto', 'normal');

  let yPosition = 20;

  // Header
  doc.setFontSize(20);
  doc.text('PropertyCheck.PRO', 20, yPosition);
  yPosition += 10;

  doc.setFontSize(16);
  doc.text(
    `Protokół inspekcji - ${INSPECTION_TYPE_LABELS[inspection.type] || inspection.type}`,
    20,
    yPosition
  );
  yPosition += 15;

  // Property information
  doc.setFontSize(12);
  doc.text('Nieruchomość:', 20, yPosition);
  yPosition += 7;

  doc.setFontSize(10);
  const address = formatPropertyAddress(property);
  doc.text(`Adres: ${address}`, 20, yPosition);
  yPosition += 6;
  doc.text(`Typ: ${property.type}`, 20, yPosition);
  yPosition += 10;

  // Inspection details
  doc.setFontSize(12);
  doc.text('Szczegóły inspekcji:', 20, yPosition);
  yPosition += 7;

  doc.setFontSize(10);
  doc.text(
    `Data: ${new Date(inspection.date).toLocaleDateString('pl-PL')}`,
    20,
    yPosition
  );
  yPosition += 6;
  doc.text(`Inspektor: ${inspection.inspector || 'Nie podano'}`, 20, yPosition);
  yPosition += 6;

  // Add tenant information if available
  if (inspection.tenant || (inspection.leases && inspection.leases.tenant_name)) {
    const tenantName = inspection.tenant || inspection.leases.tenant_name;
    doc.text(`Najemca: ${tenantName}`, 20, yPosition);
    yPosition += 6;
  }

  // Add lease dates if available
  if (inspection.leases && inspection.leases.start_date) {
    const startDate = new Date(inspection.leases.start_date).toLocaleDateString('pl-PL');
    const endDate = inspection.leases.end_date
      ? new Date(inspection.leases.end_date).toLocaleDateString('pl-PL')
      : 'Nie określono';
    doc.text(`Okres najmu: ${startDate} - ${endDate}`, 20, yPosition);
    yPosition += 6;
  }

  yPosition += 6;

  // Rooms and items
  if (inspection.inspection_rooms && inspection.inspection_rooms.length > 0) {
    for (const room of inspection.inspection_rooms) {
      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      doc.setFontSize(14);
          doc.text(room.name, 20, yPosition);
      yPosition += 10;

      // Create table for items
      if (room.inspection_items && room.inspection_items.length > 0) {
        const tableData = room.inspection_items.map((item) => [
          item.name,
          CONDITION_LABELS[item.condition] || item.condition,
          item.notes || '-',
          item.inspection_photos?.length || 0, // Photo count only (no embedded images)
        ]);

        autoTable(doc, {
          startY: yPosition,
          head: [['Element', 'Stan', 'Uwagi', 'Zdjęcia']],
          body: tableData,
          theme: 'striped',
          headStyles: {
            fillColor: [147, 51, 234], // violet-600
            font: 'Roboto',
            fontStyle: 'normal'
          },
          styles: {
            fontSize: 9,
            cellPadding: 3,
            font: 'Roboto',
            fontStyle: 'normal'
          },
          columnStyles: {
            0: { cellWidth: 50 },
            1: { cellWidth: 30 },
            2: { cellWidth: 70 },
            3: { cellWidth: 20, halign: 'center' },
          },
        });

        yPosition = doc.lastAutoTable.finalY + 10;
      }
    }
  }

  // Overall notes
  if (inspection.overall_notes) {
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
      doc.text('Uwagi ogólne:', 20, yPosition);
    yPosition += 7;

      doc.setFontSize(10);
    const splitNotes = doc.splitTextToSize(inspection.overall_notes, 170);
    doc.text(splitNotes, 20, yPosition);
  }

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text(
      `Strona ${i} z ${pageCount} | Wygenerowano: ${new Date().toLocaleString('pl-PL')}`,
      20,
      doc.internal.pageSize.height - 10
    );
  }

  // Download
  const fileName = `inspekcja_${property.type}_${inspection.date}.pdf`;
  doc.save(fileName);
}

/**
 * Generate comparison PDF for two inspections
 * @param {Object} inspection1 - First inspection (before)
 * @param {Object} inspection2 - Second inspection (after)
 * @param {Object} property - Property data
 * @returns {Promise<void>} Downloads PDF file
 */
export async function generateComparisonPDF(inspection1, inspection2, property) {
  const doc = new jsPDF();

  // Load Roboto font for Polish character support
  loadRobotoFont(doc);
  doc.setFont('Roboto', 'normal');

  let yPosition = 20;

  // Header
  doc.setFontSize(20);
  doc.text('PropertyCheck.PRO', 20, yPosition);
  yPosition += 10;

  doc.setFontSize(16);
  doc.text('Raport porównawczy inspekcji', 20, yPosition);
  yPosition += 15;

  // Property information
  doc.setFontSize(12);
  doc.text('Nieruchomość:', 20, yPosition);
  yPosition += 7;

  doc.setFontSize(10);
  const address = formatPropertyAddress(property);
  doc.text(`Adres: ${address}`, 20, yPosition);
  yPosition += 6;
  doc.text(`Typ: ${property.type}`, 20, yPosition);
  yPosition += 10;

  // Comparison details
  doc.setFontSize(12);
  doc.text('Porównywane inspekcje:', 20, yPosition);
  yPosition += 7;

  doc.setFontSize(10);
  doc.text(
    `Przed: ${INSPECTION_TYPE_LABELS[inspection1.type] || inspection1.type} - ${new Date(inspection1.date).toLocaleDateString('pl-PL')}`,
    20,
    yPosition
  );
  yPosition += 6;
  doc.text(
    `Po: ${INSPECTION_TYPE_LABELS[inspection2.type] || inspection2.type} - ${new Date(inspection2.date).toLocaleDateString('pl-PL')}`,
    20,
    yPosition
  );
  yPosition += 12;

  // Overall notes from both inspections
  if (inspection1.overall_notes || inspection2.overall_notes) {
    if (yPosition > 230) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(12);
    doc.text('Uwagi ogólne:', 20, yPosition);
    yPosition += 7;

    doc.setFontSize(10);

    if (inspection1.overall_notes) {
      doc.setTextColor(139, 92, 246); // violet-600
      doc.text('Przed:', 20, yPosition);
      doc.setTextColor(0);
      yPosition += 5;
      const splitNotes1 = doc.splitTextToSize(inspection1.overall_notes, 170);
      doc.text(splitNotes1, 25, yPosition);
      yPosition += (splitNotes1.length * 5) + 5;
    }

    if (inspection2.overall_notes) {
      doc.setTextColor(16, 185, 129); // emerald-600
      doc.text('Po:', 20, yPosition);
      doc.setTextColor(0);
      yPosition += 5;
      const splitNotes2 = doc.splitTextToSize(inspection2.overall_notes, 170);
      doc.text(splitNotes2, 25, yPosition);
      yPosition += (splitNotes2.length * 5) + 5;
    }

    yPosition += 5;
  }

  // Iterate through rooms and find changes
  for (let roomIndex = 0; roomIndex < inspection1.inspection_rooms.length; roomIndex++) {
    const room1 = inspection1.inspection_rooms[roomIndex];
    const room2 = inspection2.inspection_rooms[roomIndex];

    if (!room2) continue;

    // Find items with changes
    const changedItems = [];
    for (let itemIndex = 0; itemIndex < room1.inspection_items.length; itemIndex++) {
      const item1 = room1.inspection_items[itemIndex];
      const item2 = room2.inspection_items[itemIndex];

      if (!item2) continue;

      if (item1.condition !== item2.condition) {
        changedItems.push({
          item1,
          item2,
        });
      }
    }

    // Skip room if no changes
    if (changedItems.length === 0) continue;

    // Check if we need a new page
    if (yPosition > 250) {
      doc.addPage();
      yPosition = 20;
    }

    doc.setFontSize(14);
    doc.text(room1.name, 20, yPosition);
    yPosition += 10;

    // Process each changed item
    for (const { item1, item2 } of changedItems) {
      // Check if we need a new page
      if (yPosition > 240) {
        doc.addPage();
        yPosition = 20;
      }

      // Item name
      doc.setFontSize(11);
      doc.text(`• ${item1.name}`, 25, yPosition);
      yPosition += 7;

      // Before/After conditions and notes in a table
      const tableData = [
        [
          CONDITION_LABELS[item1.condition] || item1.condition,
          CONDITION_LABELS[item2.condition] || item2.condition,
        ],
      ];

      // Add notes row if either has notes
      if (item1.notes || item2.notes) {
        tableData.push([
          item1.notes || '-',
          item2.notes || '-',
        ]);
      }

      const headers = item1.notes || item2.notes ? [['Stan (Przed)', 'Stan (Po)'], ['Uwagi (Przed)', 'Uwagi (Po)']] : [['Przed', 'Po']];

      autoTable(doc, {
        startY: yPosition,
        head: item1.notes || item2.notes ? [['', 'Przed', 'Po']] : [['Przed', 'Po']],
        body: item1.notes || item2.notes ? [
          ['Stan', CONDITION_LABELS[item1.condition] || item1.condition, CONDITION_LABELS[item2.condition] || item2.condition],
          ['Uwagi', item1.notes || '-', item2.notes || '-']
        ] : tableData,
        theme: 'grid',
        headStyles: {
          fillColor: [147, 51, 234],
          font: 'Roboto',
          fontStyle: 'normal',
          fontSize: 9,
        },
        styles: {
          fontSize: 9,
          cellPadding: 3,
          font: 'Roboto',
          fontStyle: 'normal',
        },
        columnStyles: item1.notes || item2.notes ? {
          0: { cellWidth: 25, fontStyle: 'bold' },
          1: { cellWidth: 72.5, fillColor: [237, 233, 254] }, // violet-100
          2: { cellWidth: 72.5, fillColor: [209, 250, 229] }, // emerald-100
        } : {
          0: { cellWidth: 85, fillColor: [237, 233, 254] }, // violet-100
          1: { cellWidth: 85, fillColor: [209, 250, 229] }, // emerald-100
        },
        margin: { left: 25 },
      });

      yPosition = doc.lastAutoTable.finalY + 5;

      // Add photo thumbnails if available
      const photos1 = item1.inspection_photos || [];
      const photos2 = item2.inspection_photos || [];

      if (photos1.length > 0 || photos2.length > 0) {
        // Check if we have enough space for photos
        if (yPosition > 220) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFontSize(9);
        doc.text('Zdjęcia:', 25, yPosition);
        yPosition += 5;

        const photoY = yPosition;
        let xPosition = 30;

        // Before photos
        if (photos1.length > 0) {
          doc.setFontSize(8);
          doc.text('Przed:', xPosition, photoY);

          for (let i = 0; i < Math.min(photos1.length, 2); i++) {
            const photo = photos1[i];
            if (photo.url) {
              try {
                // Load image and add to PDF
                const img = await loadImageFromUrl(photo.url);
                doc.addImage(img, 'JPEG', xPosition, photoY + 2, 30, 30);
                xPosition += 32;
              } catch (error) {
                console.error('Failed to load image:', error);
                // Continue without the image
              }
            }
          }
        }

        xPosition = 110;

        // After photos
        if (photos2.length > 0) {
          doc.setFontSize(8);
          doc.text('Po:', xPosition, photoY);

          for (let i = 0; i < Math.min(photos2.length, 2); i++) {
            const photo = photos2[i];
            if (photo.url) {
              try {
                const img = await loadImageFromUrl(photo.url);
                doc.addImage(img, 'JPEG', xPosition, photoY + 2, 30, 30);
                xPosition += 32;
              } catch (error) {
                console.error('Failed to load image:', error);
              }
            }
          }
        }

        yPosition = photoY + 35;
      }

      yPosition += 5;
    }

    yPosition += 5;
  }

  // Summary section
  if (yPosition > 240) {
    doc.addPage();
    yPosition = 20;
  }

  doc.setFontSize(12);
  doc.text('Podsumowanie:', 20, yPosition);
  yPosition += 7;

  // Count total changes
  let totalChanges = 0;
  for (const room1 of inspection1.inspection_rooms) {
    const room2 = inspection2.inspection_rooms.find(r => r.name === room1.name);
    if (!room2) continue;

    for (const item1 of room1.inspection_items) {
      const item2 = room2.inspection_items.find(i => i.name === item1.name);
      if (!item2) continue;

      if (item1.condition !== item2.condition) {
        totalChanges++;
      }
    }
  }

  doc.setFontSize(10);
  doc.text(`Liczba wykrytych zmian: ${totalChanges}`, 20, yPosition);
  yPosition += 6;
  doc.text(
    `Data wygenerowania: ${new Date().toLocaleString('pl-PL')}`,
    20,
    yPosition
  );

  // Footer on all pages
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(128);
    doc.text(
      `Strona ${i} z ${pageCount} | Wygenerowano: ${new Date().toLocaleString('pl-PL')}`,
      20,
      doc.internal.pageSize.height - 10
    );
  }

  // Download
  const fileName = `porownanie_${inspection1.date}_vs_${inspection2.date}.pdf`;
  doc.save(fileName);
}

/**
 * Load image from URL and convert to data URL for jsPDF
 * @param {string} url - Image URL
 * @returns {Promise<string>} Data URL
 */
async function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);

      const dataURL = canvas.toDataURL('image/jpeg', 0.8);
      resolve(dataURL);
    };

    img.onerror = (error) => {
      reject(error);
    };

    img.src = url;
  });
}
